import React, { useState, useEffect } from 'react';
import { Routes, Route, Link, useNavigate, useParams } from 'react-router-dom';
import { Plus, Pencil, Trash2, Search, ArrowLeft, Loader2, Star, X } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import ImageUpload from '../../components/ui/ImageUpload';
import { productsAPI, categoriesAPI } from '../../services/api';

const ProductsList = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await productsAPI.getAll();
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    p.category.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDelete = async () => {
    try {
      await productsAPI.delete(deleteId);
      setProducts(products.filter(p => p.id !== deleteId));
      setDeleteId(null);
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  const toggleFeatured = async (product) => {
    try {
      await productsAPI.update(product.id, { featured: !product.featured });
      setProducts(products.map(p => 
        p.id === product.id ? { ...p, featured: !p.featured } : p
      ));
    } catch (error) {
      console.error('Error updating product:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Products</h1>
          <p className="text-zinc-400">{products.length} total products</p>
        </div>
        <Button
          onClick={() => navigate('/admin/products/new')}
          className="bg-amber-500 hover:bg-amber-600 text-black"
        >
          <Plus className="h-4 w-4 mr-2" />
          Add Product
        </Button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-400" />
        <Input
          placeholder="Search products..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-zinc-800 border-zinc-700 text-white"
        />
      </div>

      {/* Products list */}
      <div className="space-y-3">
        {filteredProducts.map((product) => (
          <div
            key={product.id}
            className="bg-zinc-900 rounded-xl border border-zinc-800 p-4 flex items-center gap-4"
          >
            <img
              src={product.image}
              alt={product.name}
              className="w-16 h-16 rounded-lg object-cover"
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-white font-medium truncate">{product.name}</h3>
                {product.featured && (
                  <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                )}
                {product.badge && (
                  <Badge className={product.badge === 'NEW' ? 'bg-emerald-500' : 'bg-amber-500'}>
                    {product.badge}
                  </Badge>
                )}
              </div>
              <p className="text-zinc-400 text-sm">{product.category.replace(/-/g, ' ')}</p>
              <div className="flex items-center gap-2">
                <p className="text-amber-500 font-semibold">£{product.price.toFixed(2)}</p>
                {product.variations && product.variations.length > 0 && (
                  <span className="text-zinc-500 text-sm">+ {product.variations.length} variations</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                className={`border-zinc-700 ${product.featured ? 'text-amber-500' : 'text-zinc-400'} hover:text-amber-500`}
                onClick={() => toggleFeatured(product)}
                title={product.featured ? 'Remove from featured' : 'Add to featured'}
              >
                <Star className={`h-4 w-4 ${product.featured ? 'fill-amber-500' : ''}`} />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-zinc-700 text-zinc-400 hover:text-white"
                onClick={() => navigate(`/admin/products/edit/${product.id}`)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                className="border-zinc-700 text-red-400 hover:text-red-300 hover:border-red-500/50"
                onClick={() => setDeleteId(product.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
        ))}

        {filteredProducts.length === 0 && (
          <div className="text-center py-12">
            <p className="text-zinc-400">No products found</p>
          </div>
        )}
      </div>

      {/* Delete confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent className="bg-zinc-900 border-zinc-800">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Product</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete this product? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-zinc-700 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const ProductForm = ({ isEdit = false }) => {
  const navigate = useNavigate();
  const { id: productId } = useParams();
  
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    category: '',
    price: '',
    description: '',
    shortDescription: '',
    image: '',
    badge: '',
    inStock: true,
    featured: false,
    variations: [],
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        const categoriesRes = await categoriesAPI.getAll();
        setCategories(categoriesRes.data);

        if (isEdit && productId) {
          const productsRes = await productsAPI.getAll();
          const product = productsRes.data.find(p => p.id === productId);
          if (product) {
            setFormData({
              name: product.name,
              slug: product.slug,
              category: product.category,
              price: product.price.toString(),
              description: product.description,
              shortDescription: product.shortDescription,
              image: product.image,
              badge: product.badge || '',
              inStock: product.inStock,
              featured: product.featured || false,
              variations: product.variations || [],
            });
          }
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [isEdit, productId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
      ...(name === 'name' ? { slug: value.toLowerCase().replace(/\s+/g, '-') } : {})
    }));
  };

  const addVariation = () => {
    setFormData(prev => ({
      ...prev,
      variations: [...prev.variations, { name: '', price: '', inStock: true }]
    }));
  };

  const updateVariation = (index, field, value) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.map((v, i) => 
        i === index ? { ...v, [field]: value } : v
      )
    }));
  };

  const removeVariation = (index) => {
    setFormData(prev => ({
      ...prev,
      variations: prev.variations.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      const productData = {
        ...formData,
        price: parseFloat(formData.price),
        badge: formData.badge || null,
        variations: formData.variations.length > 0 
          ? formData.variations.map(v => ({
              ...v,
              price: parseFloat(v.price) || parseFloat(formData.price)
            }))
          : null,
      };

      if (isEdit) {
        await productsAPI.update(productId, productData);
      } else {
        await productsAPI.create(productData);
      }
      navigate('/admin/products');
    } catch (error) {
      console.error('Error saving product:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 text-amber-500 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate('/admin/products')}
          className="text-zinc-400 hover:text-white"
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-white">
            {isEdit ? 'Edit Product' : 'New Product'}
          </h1>
          <p className="text-zinc-400">
            {isEdit ? 'Update product details' : 'Add a new product to your store'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Info */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 space-y-6">
          <h3 className="text-lg font-semibold text-white">Basic Information</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-white">Product Name</Label>
              <Input
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="mt-2 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
            <div>
              <Label className="text-white">Slug</Label>
              <Input
                name="slug"
                value={formData.slug}
                onChange={handleChange}
                required
                className="mt-2 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-white">Category</Label>
              <Select
                value={formData.category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, category: value }))}
              >
                <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700 text-white">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.slug} className="text-white">
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-white">Base Price (£)</Label>
              <Input
                name="price"
                type="number"
                step="0.01"
                value={formData.price}
                onChange={handleChange}
                required
                className="mt-2 bg-zinc-800 border-zinc-700 text-white"
              />
            </div>
          </div>

          <div>
            <Label className="text-white">Short Description</Label>
            <Input
              name="shortDescription"
              value={formData.shortDescription}
              onChange={handleChange}
              required
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>

          <div>
            <Label className="text-white">Full Description</Label>
            <Textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              rows={4}
              className="mt-2 bg-zinc-800 border-zinc-700 text-white"
            />
          </div>
        </div>

        {/* Image & Display */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 space-y-6">
          <h3 className="text-lg font-semibold text-white">Image & Display</h3>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label className="text-white">Image</Label>
              <div className="mt-2">
                <ImageUpload
                  value={formData.image}
                  onChange={(url) => setFormData(prev => ({ ...prev, image: url }))}
                />
              </div>
            </div>
            <div className="space-y-6">
              <div>
                <Label className="text-white">Badge (optional)</Label>
                <Select
                  value={formData.badge || 'none'}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, badge: value === 'none' ? '' : value }))}
                >
                  <SelectTrigger className="mt-2 bg-zinc-800 border-zinc-700 text-white">
                    <SelectValue placeholder="No badge" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-800 border-zinc-700">
                    <SelectItem value="none" className="text-white">No badge</SelectItem>
                    <SelectItem value="HOT" className="text-white">HOT</SelectItem>
                    <SelectItem value="NEW" className="text-white">NEW</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">Featured Product</Label>
                  <p className="text-zinc-500 text-sm">Show in featured section on homepage</p>
                </div>
                <Switch
                  checked={formData.featured}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, featured: checked }))}
                />
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <Label className="text-white">In Stock</Label>
                  <p className="text-zinc-500 text-sm">Product is available for purchase</p>
                </div>
                <Switch
                  checked={formData.inStock}
                  onCheckedChange={(checked) => setFormData(prev => ({ ...prev, inStock: checked }))}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Variations */}
        <div className="bg-zinc-900 rounded-xl border border-zinc-800 p-6 space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold text-white">Product Variations</h3>
              <p className="text-zinc-500 text-sm">Add different versions like Vials, Pens, etc.</p>
            </div>
            <Button
              type="button"
              variant="outline"
              onClick={addVariation}
              className="border-zinc-700 text-white hover:bg-zinc-800"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Variation
            </Button>
          </div>

          {formData.variations.length > 0 ? (
            <div className="space-y-4">
              {formData.variations.map((variation, index) => (
                <div key={index} className="flex items-center gap-4 p-4 bg-zinc-800 rounded-lg">
                  <div className="flex-1">
                    <Label className="text-white text-sm">Variation Name</Label>
                    <Input
                      value={variation.name}
                      onChange={(e) => updateVariation(index, 'name', e.target.value)}
                      placeholder="e.g., 5mg Vial, Premade Pen"
                      className="mt-1 bg-zinc-700 border-zinc-600 text-white"
                    />
                  </div>
                  <div className="w-32">
                    <Label className="text-white text-sm">Price (£)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={variation.price}
                      onChange={(e) => updateVariation(index, 'price', e.target.value)}
                      placeholder={formData.price}
                      className="mt-1 bg-zinc-700 border-zinc-600 text-white"
                    />
                  </div>
                  <div className="flex items-center gap-2 pt-6">
                    <Switch
                      checked={variation.inStock}
                      onCheckedChange={(checked) => updateVariation(index, 'inStock', checked)}
                    />
                    <span className="text-zinc-400 text-sm">In Stock</span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    onClick={() => removeVariation(index)}
                    className="text-red-400 hover:text-red-300 pt-6"
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border-2 border-dashed border-zinc-800 rounded-lg">
              <p className="text-zinc-500">No variations added. Click "Add Variation" to create product options.</p>
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/admin/products')}
            className="border-zinc-700 text-white hover:bg-zinc-800"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            className="bg-amber-500 hover:bg-amber-600 text-black"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              isEdit ? 'Save Changes' : 'Create Product'
            )}
          </Button>
        </div>
      </form>
    </div>
  );
};

const ProductsManager = () => {
  return (
    <Routes>
      <Route index element={<ProductsList />} />
      <Route path="new" element={<ProductForm />} />
      <Route path="edit/:id" element={<ProductForm isEdit />} />
    </Routes>
  );
};

export default ProductsManager;
